import { useTranslation } from 'next-i18next'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Checkbox } from '@components/ui/checkbox'
import { Wand2, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

// Animation variants for smooth transitions
const fadeAnimation = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.3, ease: 'easeOut' },
}

const MagicLinkForm = ({ email, setEmail, isLoading, onSubmit, magicLinkSent, setMagicLinkSent }) => {
  const { t } = useTranslation('common')

  return (
    <AnimatePresence mode="wait">
      {magicLinkSent ? (
        <motion.div
          key="success"
          className="text-center py-4"
          variants={fadeAnimation}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <div className="bg-purple-100 rounded-full p-3 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <Wand2 className="h-10 w-10 text-purple-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('auth.magic_link_sent')}</h3>
          <p className="text-gray-600 mb-5">{t('auth.check_email')}</p>
          <Button
            variant="outline"
            onClick={() => setMagicLinkSent(false)}
            className="text-sm border-gray-300 hover:bg-gray-50 rounded-md transition-all duration-200"
          >
            {t('auth.use_different_email')}
          </Button>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          onSubmit={onSubmit}
          className="space-y-4"
          variants={fadeAnimation}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <Input
            type="email"
            placeholder={t('auth.email_input_placeholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white border-gray-300 h-11 rounded-md focus:ring-2 focus:ring-purple-200 focus:border-purple-500 transition-all duration-200"
            required
          />
          <div className="flex items-start space-x-2 py-1">
            <Checkbox id="terms-magic" required className="mt-0.5" />
            <label
              htmlFor="terms-magic"
              className="text-sm text-gray-600 leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t('auth.agree_to_terms')}
            </label>
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white h-12 rounded-md shadow-md hover:shadow-lg transition-all duration-300 font-medium disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {t('auth.send_magic_link')}
          </Button>
        </motion.form>
      )}
    </AnimatePresence>
  )
}

export default MagicLinkForm
